/* eslint-disable camelcase */
import { insights, userService } from '@saucelabs/sl-util-axios';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { getLocalTimeZone, today } from '@internationalized/date';

import { Source, DateTimeRequestParams, FilterKey, OwnerRequestParams } from './types';
import { getMinDate, getUnixTimestampInSeconds, getJSDateFromDateString } from './utils';

export interface GetUsersParams {
  username?: string;
  teams?: string;
  phrase?: string;
  roles?: string;
  team_name?: string;
  status?: string;
  ordering?: string;
  limit?: number;
  offset?: number;
}

export interface User {
  id: string;
  is_active?: boolean;
  username?: string;
}

export interface GetUsersResponse {
  links: {
    next: string | null;
    previous: string | null;
    first: string | null;
    last: string | null;
  };
  count: number;
  results: User[];
}

export interface GetTeamsParams {
  limit?: number;
  offset?: number;
}

export interface Team {
  id: string;
  name?: string;
}

export interface GetTeamsResponse {
  links: {
    next: string | null;
    previous: string | null;
    first: string | null;
    last: string | null;
  };
  results: Team[];
}

export interface GetFiltersParams extends DateTimeRequestParams, OwnerRequestParams {
  source: Source;
}

export type GetFiltersResponse = Partial<
  Record<
    FilterKey,
    {
      name: string;
      count: number;
    }[]
  >
>;

export const getUsers = (
  params: GetUsersParams,
): Promise<AxiosResponse<GetUsersResponse>> => userService.get<GetUsersResponse>('/v1/users/', { params });

export const getTeams = (
  params: GetTeamsParams,
): Promise<AxiosResponse<GetTeamsResponse>> => userService.get<GetTeamsResponse>('/v1/teams/', { params });

export const getFilters = (
  config: AxiosRequestConfig,
): Promise<AxiosResponse<GetFiltersResponse>> => {
  const { params } = config;
  const { source, ...rest } = params as GetFiltersParams;

  // we need to make sure the API is not requested with dates smaller than 30 days ago
  // this can happen because the synchronized <FilterBar /> always fetch filters
  // regardless if needed or not
  // e.g. for "Usage Analytics" we want to request up to year of data
  // and since everything is synchronized this call will crash
  // and the logic to call further APIs will stop
  const minDate = getUnixTimestampInSeconds(getMinDate().toDate(getLocalTimeZone()));
  if (rest.start) {
    const startDate = rest.start ??
      getUnixTimestampInSeconds(getJSDateFromDateString(new Date().toISOString()));

    rest.start = (startDate < minDate) ? minDate : params.start;
  }
  if (rest.end) {
    const now = getUnixTimestampInSeconds(today(getLocalTimeZone()).toDate(getLocalTimeZone()));
    const endDate = rest.end ?? now;

    rest.end = (endDate < minDate) ? now : params.end;
  }

  return insights.get(`/${source}/filters`, { ...config, params: rest });
};
